import { useEffect, useRef } from "react";
import { Flex, Form } from "antd";
import { useParams } from "react-router-dom";
import {
  useApiUrl,
  useCustom,
  useCustomMutation,
  useTranslate,
} from "@refinedev/core";
import { useDebouncedCallback } from "use-debounce";
import { AlertCard, Skeleton } from "@libertify/ui";
import type { ProjectAddOnSettingsPatchRequest } from "pages/media/types";
import { useProjectInstance } from "pages/media/projects/hooks/useProjectInstance";
import { PlaybookAddOnCard } from "../PlaybookAddOnCard";
import { CONFIG_ITEM } from "../../data/addons";
import {
  hasIncompletePodcastVoiceIds,
  type ProjectAddOnsResponse,
} from "../../data/addonApi";
import { PODCAST_DEFAULTS } from "./defaults";
import type { PodcastFormValues } from "./types";
import { useAudioConfigData } from "./hooks/useAudioConfigData";
import { PodcastSettingsFields } from "./podcast/PodcastSettingsFields";

const KEY = "experienceEdit.podcastCard";

/** Coalesces a run of field changes into one request. */
const SAVE_DELAY = 600;

export interface PodcastEditCardProps {
  /** Whether the podcast is shown to viewers of the experience. */
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  visiblePending?: boolean;
}

/**
 * Podcast card for the experience edit page. Where `PodcastConfigCard`
 * collects settings in the draft before the podcast exists, this one reads
 * them off the project and saves each change — there's no save button.
 *
 * Unlike the chat edit card (functional settings only), voice and duration
 * here feed generation directly, so every save marks the live publication out
 * of date. Regeneration itself is a separate action, triggered elsewhere.
 *
 * Renders the shared `PodcastSettingsFields` minus the target duration, which
 * isn't editable here. antd only reports registered fields, so the stored
 * duration is merged back into the PATCH body (like `ChatbotSettingsForm`'s
 * edit variant does for its hidden fields) and a save never drops it.
 */
export const PodcastEditCard = ({
  visible,
  onVisibleChange,
  visiblePending,
}: PodcastEditCardProps) => {
  const t = useTranslate();
  const { projectId, instanceId } = useParams<{
    projectId: string;
    instanceId?: string;
  }>();
  const apiUrl = useApiUrl();
  const addOnsUrl = `${apiUrl}/media/projects/${projectId}/add-ons`;
  const [form] = Form.useForm<PodcastFormValues>();

  // Same read the page makes for its rail, so this shares its cache.
  const { data, isLoading, refetch } = useCustom<ProjectAddOnsResponse>({
    url: addOnsUrl,
    method: "get",
    queryOptions: { enabled: Boolean(projectId) },
  });

  // Mirrors ExperienceEditPage and GeneralSettingsEditCard: the route's
  // :instanceId is optional, and "default" is the instance it falls back to.
  const { markAsOutOfDate } = useProjectInstance({
    projectId: projectId ?? "",
    instanceId: instanceId ?? "default",
  });

  // Emotion-capable voices only: the podcast script carries emotion tags and
  // generation fails outright on a voice that can't speak them (same
  // restriction as the create-flow card).
  const { emotionCapableAvatarOptions, avatarsLoading } = useAudioConfigData();

  const stored = data?.data.settings;
  const initialValues: PodcastFormValues = {
    generation_settings: {
      ...PODCAST_DEFAULTS.generation_settings,
      ...stored?.podcast_generation_settings,
    },
    functional_settings: {
      ...PODCAST_DEFAULTS.functional_settings,
      ...stored?.podcast_functional_settings,
    },
  };

  const saved = useRef<PodcastFormValues>(initialValues);
  const mounted = useRef(true);
  // Sticky across a debounced batch of changes: set whenever a functional
  // field changes, consumed (and reset) the next time save actually fires —
  // useDebouncedCallback only keeps the trailing call's arguments, so a
  // functional change followed by a generation-only change within the same
  // window would otherwise be lost.
  const hasFunctionalChangeRef = useRef(false);
  // Bumped on every save attempt. Lets a save's failure handler tell whether
  // a newer save has started (and possibly already succeeded) since — if so,
  // reverting to this attempt's `previous` would clobber that newer state.
  const requestIdRef = useRef(0);

  // `saved.current` is only assigned once as `useRef`'s initial value, so
  // without this it stays frozen on the defaults-only `initialValues` computed
  // before `data` loads — a later failed save would then revert the form to
  // those defaults instead of the actual last-known-good settings.
  useEffect(() => {
    saved.current = initialValues;
    // `stored` (not `initialValues`, which is a fresh object every render) is
    // the actual signal that new server data landed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored]);

  // The save currently running, so showing the podcast can wait for a voice
  // that's still on its way to the server.
  const inFlightSave = useRef<Promise<void>>(Promise.resolve());

  const { mutateAsync } = useCustomMutation();
  const saveNow = async (values: PodcastFormValues) => {
    const requestId = ++requestIdRef.current;
    const previous = saved.current;
    // Each role writes to its own array index, so filling only the second role
    // would leave a hole (`[undefined, id]`) that serializes to `null` and is
    // rejected by the API. Compact it before it's sent.
    const storedDuration = previous.generation_settings.target_duration_in_s;
    const next: PodcastFormValues = {
      ...values,
      generation_settings: {
        ...values.generation_settings,
        ...(storedDuration === undefined
          ? {}
          : { target_duration_in_s: storedDuration }),
        voice_profile_ids: (
          values.generation_settings.voice_profile_ids ?? []
        ).filter(Boolean),
      },
    };
    // Voice/duration feed generation, and there's no regenerate action wired
    // up here yet — flagging the publication stale for those would be wrong,
    // since a republish alone wouldn't pick up a voice that never regenerated.
    // Functional settings (playback speed, auto-play) apply immediately, so
    // those genuinely do make the live publication stale.
    const markStale = hasFunctionalChangeRef.current;
    hasFunctionalChangeRef.current = false;
    try {
      await mutateAsync({
        url: `${addOnsUrl}/settings`,
        method: "patch",
        values: {
          add_on_type: CONFIG_ITEM.Podcast,
          // `background_music` is optional here (dropped from the card), but
          // required by the schema's `PodcastGenerationSettings` — same cast
          // `mapDraftToSettingsPayloads` uses for the same reason.
          generation_settings:
            next.generation_settings as ProjectAddOnSettingsPatchRequest["generation_settings"],
          functional_settings: next.functional_settings,
        } satisfies ProjectAddOnSettingsPatchRequest,
        successNotification: false,
      });
    } catch {
      // useCustomMutation raises the error notification itself. Detached
      // form: nothing to revert, and setFieldsValue would warn. A newer save
      // may have started (and already succeeded) since this one did — only
      // revert if this is still the latest attempt, or it would clobber that
      // newer state with this attempt's now-stale snapshot.
      if (mounted.current && requestIdRef.current === requestId) {
        form.setFieldsValue(previous);
      }
      return;
    }
    saved.current = next;
    // useCustomMutation touches no cache, and the card remounts on every rail
    // switch — without this it reseeds from the pre-save read and the next
    // change PATCHes those stale values back over this one.
    await refetch();
    if (markStale) await markAsOutOfDate().catch(() => undefined);
  };
  const save = useDebouncedCallback((values: PodcastFormValues) => {
    inFlightSave.current = saveNow(values);
  }, SAVE_DELAY);

  const handleVisibleChange = async (next: boolean) => {
    if (next) {
      save.flush();
      await inFlightSave.current;
    }
    onVisibleChange?.(next);
  };

  // use-debounce drops a pending call on unmount, and the card unmounts on
  // every rail switch — flush, or the last change silently never saves. Set on
  // the way in too: a remount reuses the ref, and a stale `false` would leave
  // the revert above permanently dead.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      void save.flush();
    };
  }, [save]);

  return (
    <PlaybookAddOnCard
      itemId={CONFIG_ITEM.Podcast}
      title={t(`${KEY}.title`)}
      description={t(`${KEY}.subtitle`)}
      visible={visible}
      visiblePending={visiblePending}
      onVisibleChange={
        onVisibleChange
          ? (next) => void handleVisibleChange(next)
          : undefined
      }
    >
      {/* Mounted once the settings are in, so the form seeds from them. */}
      {isLoading && projectId ? (
        <Skeleton variant="text" rows={6} />
      ) : (
        <Flex vertical gap={16}>
          {/* The page won't show the podcast until both voices are set. */}
          {hasIncompletePodcastVoiceIds(
            stored?.podcast_generation_settings.voice_profile_ids
          ) ? (
            <AlertCard type="info" title={t(`${KEY}.voicesRequired`)} />
          ) : null}
          <Form<PodcastFormValues>
            form={form}
            layout="vertical"
            initialValues={initialValues}
            onValuesChange={(changedValues, values) => {
              if (changedValues.functional_settings) {
                hasFunctionalChangeRef.current = true;
              }
              void save(values);
            }}
          >
            <PodcastSettingsFields
              voiceOptions={emotionCapableAvatarOptions}
              voicesLoading={avatarsLoading}
              initialVoiceProfileIds={
                initialValues.generation_settings.voice_profile_ids
              }
              hideTargetDuration
            />
          </Form>
        </Flex>
      )}
    </PlaybookAddOnCard>
  );
};

export default PodcastEditCard;
